import type { ResolvedDateTimeFormat } from "@/shared/datetime";
import { Popover } from "@/shared/foundation/primitives";

import { formatRange } from "./formatRange";
import type { TimelineTrackItem } from "./TimelineTrackItem";
import styles from "./TimelineTracks.module.css";

interface TrackHoverCardProps<T> {
  /** The hovered or focused bar and its item; `null` closes the card. */
  hover: { item: TimelineTrackItem<T>; bar: HTMLElement } | null;
  /** Reads the item's start and end, on the timeline's clock. */
  format: ResolvedDateTimeFormat;
  /** Escape or a press outside the card. */
  onClose: () => void;
}

/**
 * Label, start, end and duration of the hovered or focused bar, in a popover
 * anchored to the bar. The bar opens and closes it, so it has no trigger of
 * its own. Focus stays on the bar: keyboard users step through bars with the
 * card following, and the bar's accessible name already carries the range,
 * so the card is hidden from the accessibility tree rather than announced as
 * a dialog.
 */
export function TrackHoverCard<T>({
  hover,
  format,
  onClose,
}: TrackHoverCardProps<T>) {
  return (
    <Popover
      open={hover !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      {hover && (
        <Popover.Content
          anchor={hover.bar}
          initialFocus={false}
          finalFocus={false}
          role="presentation"
          aria-hidden
          className={styles.hoverCard}
        >
          <HoverCardFacts item={hover.item} format={format} />
        </Popover.Content>
      )}
    </Popover>
  );
}

function HoverCardFacts<T>({
  item,
  format,
}: {
  item: TimelineTrackItem<T>;
  format: ResolvedDateTimeFormat;
}) {
  const range = formatRange(item, format);
  return (
    <>
      <p className={styles.hoverCardTitle}>{item.label}</p>
      <dl className={styles.hoverCardFacts}>
        <dt>Start</dt>
        <dd>{range.start}</dd>
        <dt>End</dt>
        <dd>{range.end}</dd>
        <dt>Duration</dt>
        <dd>{range.duration}</dd>
      </dl>
    </>
  );
}
